import noisePng from "./noise.png";
import noiseAvif from "./noise@20aq.avif";
import { cn } from "@ui/lib/utils";
import { cva } from "class-variance-authority";
import ReactDOM from "react-dom";

export type NoiseVariant = "static" | "dynamic";
type NoiseProps = Omit<React.ComponentPropsWithRef<"div">, "children"> & {
  priority?: boolean;
  scale?: number;
  variant?: NoiseVariant;
};

const noiseClassName = cva("tw-absolute tw-mix-blend-overlay", {
  variants: {
    variant: {
      static:
        "tw-inset-0 tw-bg-[image:var(--noise-png)] tw-bg-[length:calc(256px*var(--noise-scale))] [@media(min-resolution:2x)]:tw-bg-[length:calc(128px*var(--noise-scale))]",
      dynamic: "tw-inset-0 tw-overflow-hidden",
    },
  },
  defaultVariants: {
    variant: "static",
  },
});

const dynamicLayerClassName =
  "tw-absolute -tw-inset-1/2 tw-size-[200%] tw-animate-noise-drift tw-bg-[image:var(--noise-png)] tw-bg-[length:calc(256px*var(--noise-scale))] tw-will-change-transform motion-reduce:tw-animate-none motion-reduce:tw-transform-none [@media(min-resolution:2x)]:tw-bg-[length:calc(128px*var(--noise-scale))]";

/**
 * Noise overlay that stretches to fill its closest positioned ancestor
 */
export function Noise({ priority = false, scale = 1, variant = "static", ...props }: NoiseProps) {
  if (priority) {
    ReactDOM.preload(noiseAvif.src, { as: "image", type: "image/avif" });
  }

  const backgroundImage = `image-set(url(${noiseAvif.src}) type('image/avif'), var(--noise-png) type('image/png'))`;
  const sharedStyle = {
    "--noise-scale": scale,
    "--noise-png": `url(${noisePng.src})`,
    imageRendering: "pixelated",
  } as React.CSSProperties;

  return (
    <div
      data-vrt-hide
      {...props}
      className={cn(noiseClassName({ variant }), props.className)}
      style={
        {
          ...sharedStyle,
          backgroundImage: variant === "static" ? backgroundImage : undefined,
          ...props.style,
        } as React.CSSProperties
      }
    >
      {variant === "dynamic" && (
        <div
          aria-hidden
          className={dynamicLayerClassName}
          style={{
            ...sharedStyle,
            backgroundImage,
          }}
        />
      )}
    </div>
  );
}
